import { FormControl, InputAdornment, MenuItem, Stack, Typography } from "@mui/material";
import { CustomInputLabel } from "component/CustomInputLabel";
import { ErrorAlert } from "component/ErrorAlert";
import { ErrorText } from "component/ErrorMessage";
import { FormRadioGroup } from "component/FormRadioGroup";
import { FormSelect } from "component/FormSelect";
import { AlignJustifyIcon, ChevronDownIcon, DoubleArrowUpIcon } from "component/Icons";
import { MEDICAL_TEAM_OPTIONS, PRIORITY_SELECT_ITEMS, SPECIAL_CONDITION_SELECT_ITEMS } from "constant";
import { Fragment } from "react";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";
import { COLORS } from "style";
import { Hospital } from "type/model/api";
import { CreateSecondaryMissionFormProps } from "type/schema";
import { CustomSelect } from "../../component/CustomSelect";

interface Props {
  hospitals: Hospital[];
}

export const SinglePatientDetails = ({ hospitals }: Props) => {
  const { control } = useFormContext<Partial<CreateSecondaryMissionFormProps>>();
  const { fields } = useFieldArray({ name: "tasks" });

  if (fields.length < 1) {
    return <ErrorAlert>Invalid form state</ErrorAlert>;
  }

  return (
    <Fragment key={fields[0].id}>
      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <Controller
          name="tasks.0.priority"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <FormSelect
              label="Priority"
              required
              placeholder="Select a priority"
              displayEmpty={true}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={error?.message}
              sx={{ minWidth: 350 }}
              startAdornment={
                <InputAdornment position="start">
                  <DoubleArrowUpIcon />
                </InputAdornment>
              }
              items={PRIORITY_SELECT_ITEMS}
            />
          )}
        />
        <Controller
          name="specialCondition"
          control={control}
          render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
            <FormSelect
              label="Special conditions"
              placeholder="Select a mission condition"
              displayEmpty={true}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              error={error?.message}
              sx={{ minWidth: 350 }}
              startAdornment={
                <InputAdornment position="start">
                  <AlignJustifyIcon />
                </InputAdornment>
              }
              items={SPECIAL_CONDITION_SELECT_ITEMS}
            />
          )}
        />
      </Stack>

      <Stack direction="row" sx={{ width: "100%", justifyContent: "space-between" }}>
        <Controller
          name="tasks.0.referringHospital"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl>
              <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                Referring Hospital
              </CustomInputLabel>
              <CustomSelect
                displayEmpty={true}
                renderValue={(value) => {
                  const hospital = hospitals.find((hospital) => hospital.id === value);
                  return hospital ? hospital.name : "Select a hospital";
                }}
                {...field}
                size="small"
                sx={{ minWidth: 350 }}
                variant="outlined"
                IconComponent={ChevronDownIcon}
                MenuProps={{
                  disableAutoFocusItem: true,
                  MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                }}
              >
                {hospitals.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </CustomSelect>
              {error ? (
                <Stack sx={{ mt: 1, maxWidth: 350 }}>
                  <ErrorText>{error.message}</ErrorText>
                </Stack>
              ) : null}
            </FormControl>
          )}
        />
        <Controller
          name="tasks.0.destinationHospital"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl>
              <CustomInputLabel sx={{ marginBottom: "8px" }} required>
                Destination Hospital
              </CustomInputLabel>
              <CustomSelect
                displayEmpty={true}
                renderValue={(value) => {
                  const hospital = hospitals.find((hospital) => hospital.id === value);
                  return hospital ? hospital.name : "Select a hospital";
                }}
                {...field}
                size="small"
                sx={{ minWidth: 350 }}
                variant="outlined"
                IconComponent={ChevronDownIcon}
                MenuProps={{
                  disableAutoFocusItem: true,
                  MenuListProps: { sx: { bgcolor: COLORS.DR_WHITE } },
                }}
              >
                {hospitals.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name}
                  </MenuItem>
                ))}
              </CustomSelect>
              {error ? (
                <Stack sx={{ mt: 1, maxWidth: 350 }}>
                  <ErrorText>{error.message}</ErrorText>
                </Stack>
              ) : null}
            </FormControl>
          )}
        />
      </Stack>
      <Stack sx={{ gap: 1 }}>
        <Typography variant="h2">Medical team</Typography>
        <Typography>Select the medical team members</Typography>
      </Stack>
      <Controller
        name="tasks.0.medicalTeam"
        control={control}
        render={({ field: { onChange, value, onBlur }, fieldState: { error } }) => (
          <FormRadioGroup
            fullWidth
            row
            label="Medical team"
            required
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={error?.message}
            items={MEDICAL_TEAM_OPTIONS}
          />
        )}
      />
    </Fragment>
  );
};
